import React, { useEffect, useState, useRef } from 'react';
import { Unity, useUnityContext } from "react-unity-webgl";
import styles from '../../static/styles/AimTrain.module.css';
import MessagePopup from '../../components/MessagePopup';
import GameRatingPopup from '../../components/GameRatingPopup';
import loadingImg from '../../static/img/MadeByESL/coverAimTrainTour.avif'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { submitGameExit } from '../../api/games';
import { eslTourMatchResult, eslGameRatings } from '../../api/tournaments';


const AimTrainStg = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [showRotateMessage, setShowRotateMessage] = useState(false);
  const [contextError, setContextError] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [ratingPopupOpen, setRatingPopupOpen] = useState(false);
  const gameContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const aimTrainState = {
    tournamentMode: true,
    roomId: "Test1234",
    tournamentId: "default_tournament",
    roundNumber: 5,
    ...location.state,
    ...(searchParams.get('roundNumber') !== null && {
      roundNumber: Number(searchParams.get('roundNumber'))
    })
  };
  const isTournamentMode = true;

  const {
    unityProvider,
    isLoaded,
    sendMessage,
    requestFullscreen,
    addEventListener,
    removeEventListener,
    unload,
    loadingProgression
  } = useUnityContext({
    loaderUrl: "AimTrain/Build/AimTrain.loader.js",
    dataUrl: "AimTrain/Build/AimTrain.data.unityweb",
    frameworkUrl: "AimTrain/Build/AimTrain.framework.js.unityweb",
    codeUrl: "AimTrain/Build/AimTrain.wasm.unityweb",
    webglContextAttributes: {
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
      failIfMajorPerformanceCaveat: false,
      majorVersion: 1,
      minorVersion: 0
    }
  });

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    const handleError = (message) => {
      setContextError(true);
    };

    addEventListener("error", handleError);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && contextError) {
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      removeEventListener("error", handleError);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unload();
    };
  }, [addEventListener, removeEventListener, unload, contextError]);

  useEffect(() => {
    if (isLoaded && aimTrainState) {
      setTimeout(() => {
        try {
          if (isTournamentMode) {
            // Send room data for tournament mode
            const msisdn = sessionStorage.getItem('msisdn');
            const usrname = sessionStorage.getItem('username');
            sendMessage(
              "GameManager",
              "SetRoomData",
              JSON.stringify({
                roomName: aimTrainState.roomId,
                playerUniqueId: msisdn,
                playerName: usrname,
                isMobile: isMobile,
                roundNumber: aimTrainState.roundNumber ?? 1
              })
            );
          } else if (aimTrainState.game_unique_id) {
            // Send game level data for single player mode
            sendMessage(
              "GameManager",
              "SetGameLevel",
              JSON.stringify({
                game_unique_id: aimTrainState.game_unique_id,
                game_name: aimTrainState.game_name,
                game_level: aimTrainState.game_level,
                level_credits: aimTrainState.level_credits,
                isMobile: isMobile,
                roomName: sessionStorage.getItem('msisdn'),
                roundNumber: aimTrainState.roundNumber
              })
            );
          }
        } catch (e) { }
      }, 500);
    }
  }, [isLoaded, sendMessage, aimTrainState, isMobile, isTournamentMode]);

  useEffect(() => {
    document.title = 'AimTrain | Jazz Esl';
  })

  useEffect(() => {
    const checkOrientation = () => {
      const isLandscapeNow = window.innerWidth > window.innerHeight;
      setIsLandscape(isLandscapeNow);
      setShowRotateMessage(isMobile && !isLandscapeNow);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, [isMobile]);

  useEffect(() => {
    let fullscreenAttemptTimeout;

    const lockOrientation = async () => {
      try {
        if (window.screen && window.screen.orientation) {
          await window.screen.orientation.lock('landscape');
        }
      } catch (error) { }
    };

    const enterFullscreen = async () => {
      try {
        if (isLoaded && loadingProgression === 1) {
          requestFullscreen(true);
          setIsFullscreen(true);
        }
        else if (gameContainerRef.current && gameContainerRef.current.requestFullscreen) {
          await gameContainerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) { }
    };

    const hideAddressBar = () => {
      if ('standalone' in window.navigator && !window.navigator.standalone) {
        setTimeout(() => {
          window.scrollTo(0, 1);
        }, 100);
      }
    };

    const handleOrientationAndUI = async () => {
      clearTimeout(fullscreenAttemptTimeout);
      if (isLoaded && isMobile) {
        fullscreenAttemptTimeout = setTimeout(async () => {
          await lockOrientation();
          hideAddressBar();
          await enterFullscreen();
        }, 1000);
      }
    };

    if (isLoaded) {
      handleOrientationAndUI();
    }

    const handleUserInteraction = () => {
      if (isLoaded && isMobile && !isFullscreen) {
        enterFullscreen();
        hideAddressBar();
      }
    };

    document.addEventListener('touchend', handleUserInteraction, false);
    document.addEventListener('click', handleUserInteraction, false);

    return () => {
      clearTimeout(fullscreenAttemptTimeout);
      document.removeEventListener('touchend', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
    };
  }, [isLoaded, requestFullscreen, isMobile, isFullscreen, loadingProgression]);

  useEffect(() => {
    function handleLevelPassed(levelNumber, finalScore) {
      requestFullscreen(false);
      if (aimTrainState && !isTournamentMode) {
        submitGameExit({
          game_unique_id: aimTrainState.game_unique_id,
          game_name: aimTrainState.game_name,
          game_level: aimTrainState.game_level,
          level_credits: aimTrainState.level_credits,
          is_passed: "1"
        });
      }
      setPopupMessage(`Level ${levelNumber} completed!`);
      setPopupOpen(true);
    }

    function handleLevelFailed(levelNumber, failReason) {
      requestFullscreen(false);
      if (aimTrainState && !isTournamentMode) {
        submitGameExit({
          game_unique_id: aimTrainState.game_unique_id,
          game_name: aimTrainState.game_name,
          game_level: aimTrainState.game_level,
          level_credits: aimTrainState.level_credits,
          is_passed: "0"
        });
      }
      setPopupMessage(`Level ${levelNumber} failed! Reason: ${failReason}`);
      setPopupOpen(true);
    }

    // Tournament mode callbacks
    function handleGameEnd(result) {
      requestFullscreen(false);
      const [outcome, playerId] = result.split('|');
      //console.log(`AimTrain tournament ended: ${outcome} for player ${playerId}`);

      if (isTournamentMode && aimTrainState?.tournamentId && aimTrainState?.roomId) {
        const resultFlag = outcome === 'victory' ? 'winner' : 'loser';
        eslTourMatchResult(aimTrainState.tournamentId, aimTrainState.roomId, resultFlag)
          .then(response => {
            //console.log('Tournament result submitted:', response);
          })
          .catch(error => {
            //console.error('Failed to submit tournament result:', error);
          });
      }

      if (outcome === 'victory') {
        setPopupMessage('Victory! You won the tournament match!');
      } else {
        setPopupMessage('Defeat! Better luck next time!');
      }
      setPopupOpen(true);
    }

    function handleWaitingForOpponent() {
      setPopupMessage('Waiting for opponent to join...');
      setPopupOpen(true);
    }

    function handleGameStart() {
      setPopupMessage('Match started! Show your aim skills!');
      setPopupOpen(true);
    }

    function handleDuplicateConnection(playerId) {
      requestFullscreen(false);
      setPopupMessage('You are already connected from another device. Please close the other session first.');
      setPopupOpen(true);
    }

    // Set up callbacks based on mode
    if (isTournamentMode) {
      window.onGameEnd = handleGameEnd;
      window.onWaitingForOpponent = handleWaitingForOpponent;
      window.onGameStart = handleGameStart;
      window.onDuplicateConnection = handleDuplicateConnection;
    } else {
      window.onLevelPassed = handleLevelPassed;
      window.onLevelFailed = handleLevelFailed;
    }

    return () => {
      if (window.onLevelPassed === handleLevelPassed) window.onLevelPassed = undefined;
      if (window.onLevelFailed === handleLevelFailed) window.onLevelFailed = undefined;
      if (window.onGameEnd === handleGameEnd) window.onGameEnd = undefined;
      if (window.onWaitingForOpponent === handleWaitingForOpponent) window.onWaitingForOpponent = undefined;
      if (window.onGameStart === handleGameStart) window.onGameStart = undefined;
      if (window.onDuplicateConnection === handleDuplicateConnection) window.onDuplicateConnection = undefined;
    };
  }, [aimTrainState, requestFullscreen, isTournamentMode]);

  const handlePopupClose = () => {
    setPopupOpen(false);

    setTimeout(() => {
      setRatingPopupOpen(true);
    }, 300);
  };

  const handleFinalNavigation = () => {
    unload();
    if (isTournamentMode) {
      navigate('/tournaments');
    } else {
      navigate('/daily-gaming-tasks');
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleManualFullscreen = async () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      const element = gameContainerRef.current || document.documentElement;

      // Try different fullscreen methods based on browser
      if (element.requestFullscreen) {
        await element.requestFullscreen();
        setIsFullscreen(true);
      } else if (element.webkitRequestFullscreen) {
        // Safari
        await element.webkitRequestFullscreen();
        setIsFullscreen(true);
      } else if (element.webkitEnterFullscreen) {
        // iOS Safari video element
        await element.webkitEnterFullscreen();
        setIsFullscreen(true);
      } else if (element.mozRequestFullScreen) {
        // Firefox
        await element.mozRequestFullScreen();
        setIsFullscreen(true);
      } else if (element.msRequestFullscreen) {
        // IE/Edge
        await element.msRequestFullscreen();
        setIsFullscreen(true);
      }

      // For iOS: maximize viewport and hide address bar
      if (isIOS) {
        // Scroll to top to hide address bar
        setTimeout(() => {
          window.scrollTo(0, 1);
          document.body.scrollTop = 0;
        }, 100);

        // Try to lock orientation
        if (window.screen?.orientation?.lock) {
          try {
            await window.screen.orientation.lock('landscape');
          } catch (e) {
            console.log('Orientation lock failed:', e);
          }
        }

        // Set viewport meta for iOS
        let viewport = document.querySelector('meta[name=viewport]');
        if (viewport) {
          viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, minimal-ui';
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      // Fallback: at least try to maximize the view
      window.scrollTo(0, 1);
      setIsFullscreen(true);
    }
  };

  const TOTAL_MB = 71.4;
  const loadedMB = Math.max(0, Math.min(TOTAL_MB, Math.round((loadingProgression || 0) * TOTAL_MB * 10) / 10));

  const isPortraitMobile = isMobile && window.innerHeight > window.innerWidth;

  useEffect(() => {
    if (isMobile && !isPortraitMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        if (gameContainerRef.current) {
          gameContainerRef.current.focus?.();
        }
      }, 100);
    }
  }, [isMobile, isPortraitMobile]);

  return (
    <div
      ref={gameContainerRef}
      className={`${styles.gameContainer} ${isMobile && !isLandscape ? styles.portrait : ''}`}
    >
      {!isPortraitMobile && !contextError && (
        <div
          className={styles.unityWrapper}
          style={{
            visibility: isLoaded && (!isMobile || isLandscape || !showRotateMessage) ? 'visible' : 'hidden'
          }}
        >
          <Unity
            unityProvider={unityProvider}
            style={{ width: '100%', height: '100%' }}
            devicePixelRatio={window.devicePixelRatio}
          />
        </div>
      )}

      {contextError && (
        <div className={styles.errorOverlay}>
          <h2 className={styles.errorTitle}>Game Error</h2>
          <p className={styles.errorMessage}>
            There was an error loading the game. This may be due to WebGL compatibility issues.
          </p>
          <button className={styles.reloadButton} onClick={handleReload}>
            Reload Game
          </button>
        </div>
      )}

      {showRotateMessage && !contextError && (
        <div className={styles.rotateOverlay}>
          <div className={styles.rotateIcon}>
            <svg viewBox="0 0 100 100" width="100%" height="100%">
              <rect x="20" y="40" width="60" height="30" rx="5" stroke="white" strokeWidth="4" fill="none" />
              <circle cx="50" cy="55" r="5" stroke="white" strokeWidth="2" fill="none" />
            </svg>
          </div>
          <h2 className={styles.rotateText}>
            Please rotate your device to landscape mode
          </h2>
        </div>
      )}

      {!isLoaded && !contextError && !isPortraitMobile && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingBox}>
            <img
              src={loadingImg}
              alt="Loading AimTrain"
              className={styles.loadingImgBg}
              draggable={false}
            />
            <div className={styles.loadingProgressBarContainer} style={{
              position: 'absolute',
              bottom: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80%',
              maxWidth: '400px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div className={styles.loadingText}>
                {Math.round((loadingProgression || 0) * 100)}%
              </div>
              <div
                className={styles.loadingProgressBar}
                style={{ '--progress': `${Math.round((loadingProgression || 0) * 100)}%` }}
              />
              <div className={styles.loadingStats}>
                {loadedMB} MB / {TOTAL_MB} MB
              </div>
              {!isFullscreen && (
                <button
                  onClick={handleManualFullscreen}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: 'rgba(255, 193, 7, 0.15)',
                    color: '#ffc107',
                    border: '2px solid #ffc107',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    zIndex: 9999,
                    position: 'relative',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onMouseDown={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 193, 7, 0.3)';
                    e.target.style.transform = 'scale(0.95)';
                  }}
                  onMouseUp={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 193, 7, 0.15)';
                    e.target.style.transform = 'scale(1)';
                  }}
                  onTouchStart={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 193, 7, 0.3)';
                    e.target.style.transform = 'scale(0.95)';
                  }}
                  onTouchEnd={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 193, 7, 0.15)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  ⛶ Enter Fullscreen
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!isPortraitMobile && (
        <MessagePopup
          isOpen={popupOpen}
          message={popupMessage}
          onClose={handlePopupClose}
        />
      )}

      <GameRatingPopup
        isOpen={ratingPopupOpen}
        // isOpen={true}
        message={'Rate your match experience'}
        onClose={() => {
          setRatingPopupOpen(false);
          handleFinalNavigation();
        }}
        onSubmit={async (data) => {
          try {
            await eslGameRatings(data.rating, data.feedback, "aim_train_001");
          } catch (error) {
          } finally {
            setRatingPopupOpen(false);
            handleFinalNavigation();
          }
        }}
      />


    </div>
  );
};

export default AimTrainStg;